import React, { useState, useEffect } from 'react';
import moment from 'moment';
import { managerAPI } from '../../../config/api';
import APP_COLOURS from '../../../config/colors';

const StaffCustomers = ({ staffId, staffName }) => {
  const [loading, setLoading] = useState(true);
  const [customers, setCustomers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedCustomer, setExpandedCustomer] = useState(null);
  const [sortBy, setSortBy] = useState('recent'); // 'recent', 'name', 'sales'

  useEffect(() => {
    fetchCustomers();
  }, [staffId]);

  const fetchCustomers = async () => {
    if (!staffId) return;
    
    setLoading(true);
    try {
      console.log(`Fetching customers for staff ${staffId}`);
      const customersData = await managerAPI.getStaffCustomers(staffId);
      
      console.log('Customers data received:', customersData);
      
      // Handle different response formats
      if (Array.isArray(customersData)) {
        console.log(`Setting ${customersData.length} customer records (array format)`);
        setCustomers(customersData);
      } else if (customersData && Array.isArray(customersData.customers)) {
        console.log(`Setting ${customersData.customers.length} customer records (object.customers format)`);
        setCustomers(customersData.customers);
      } else {
        console.warn('No customer data received');
        setCustomers([]);
      }
    } catch (error) {
      console.error('Error fetching customers:', error);
      setCustomers([]);
    } finally {
      setLoading(false);
    }
  };

  const filteredCustomers = customers.filter(customer => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      customer.name?.toLowerCase().includes(query) ||
      customer.email?.toLowerCase().includes(query) ||
      customer.phone?.toLowerCase().includes(query) ||
      customer.id_number?.toLowerCase().includes(query)
    );
  });

  const sortedCustomers = [...filteredCustomers].sort((a, b) => {
    switch (sortBy) {
      case 'name':
        return (a.name || '').localeCompare(b.name || '');
      case 'sales':
        return (b.totalSales || 0) - (a.totalSales || 0);
      case 'recent':
      default:
        const dateA = a.dateCreated?.toDate ? a.dateCreated.toDate() : new Date(a.dateCreated || 0);
        const dateB = b.dateCreated?.toDate ? b.dateCreated.toDate() : new Date(b.dateCreated || 0);
        return dateB - dateA;
    }
  });

  const totalCustomers = customers.length;
  const totalSales = customers.reduce((sum, customer) => sum + (customer.totalSales || 0), 0);

  if (loading) {
    return (
      <div className="flex justify-center items-center p-12">
        <i className="fas fa-spinner fa-spin text-4xl" style={{ color: APP_COLOURS.BLUE }}></i>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <h2 className="text-2xl font-semibold mb-2" style={{ color: APP_COLOURS.TEXTCOLOR }}>
          Customers - {staffName}
        </h2>
        <p className="text-sm" style={{ color: APP_COLOURS.PLACEHOLDER }}>
          View all customers captured by this staff member
        </p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="p-4 rounded-lg" style={{ backgroundColor: APP_COLOURS.BG3, border: `1px solid ${APP_COLOURS.BG4}` }}>
          <div className="flex items-center mb-2">
            <i className="fas fa-users text-lg mr-2" style={{ color: APP_COLOURS.BLUE }}></i>
            <span className="text-sm" style={{ color: APP_COLOURS.PLACEHOLDER }}>Total Customers</span>
          </div>
          <p className="text-2xl font-bold" style={{ color: APP_COLOURS.TEXTCOLOR }}>
            {totalCustomers}
          </p>
        </div>

        <div className="p-4 rounded-lg" style={{ backgroundColor: APP_COLOURS.BG3, border: `1px solid ${APP_COLOURS.BG4}` }}>
          <div className="flex items-center mb-2">
            <i className="fas fa-cash-register text-lg mr-2" style={{ color: APP_COLOURS.BLUE }}></i>
            <span className="text-sm" style={{ color: APP_COLOURS.PLACEHOLDER }}>Total Sales Value</span>
          </div>
          <p className="text-2xl font-bold" style={{ color: APP_COLOURS.BLUE }}>
            R{totalSales.toFixed(2)}
          </p>
        </div>

        <div className="p-4 rounded-lg" style={{ backgroundColor: APP_COLOURS.BG3, border: `1px solid ${APP_COLOURS.BG4}` }}>
          <div className="flex items-center mb-2">
            <i className="fas fa-chart-line text-lg mr-2" style={{ color: APP_COLOURS.BLUE }}></i>
            <span className="text-sm" style={{ color: APP_COLOURS.PLACEHOLDER }}>Avg. Customer Value</span>
          </div>
          <p className="text-2xl font-bold" style={{ color: APP_COLOURS.TEXTCOLOR }}>
            R{totalCustomers > 0 ? (totalSales / totalCustomers).toFixed(2) : '0.00'}
          </p>
        </div>
      </div>

      {/* Controls */}
      <div className="flex flex-col md:flex-row gap-4 mb-6">
        {/* Search */}
        <div className="flex-1">
          <div className="relative">
            <i className="fas fa-search absolute left-3 top-1/2 transform -translate-y-1/2" style={{ color: APP_COLOURS.PLACEHOLDER }}></i>
            <input
              type="text"
              placeholder="Search by name, email, phone, or ID..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 rounded-lg focus:outline-none transition-all duration-200"
              style={{
                backgroundColor: APP_COLOURS.BG3,
                border: `1px solid ${APP_COLOURS.BG4}`,
                color: APP_COLOURS.TEXTCOLOR,
              }}
              onFocus={(e) => {
                e.target.style.borderColor = APP_COLOURS.BLUE;
                e.target.style.boxShadow = `0 0 0 3px rgba(0, 104, 255, 0.1)`;
              }}
              onBlur={(e) => {
                e.target.style.borderColor = APP_COLOURS.BG4;
                e.target.style.boxShadow = 'none';
              }}
            />
          </div>
        </div>

        {/* Sort */}
        <div className="flex items-center gap-2">
          <span className="text-sm whitespace-nowrap" style={{ color: APP_COLOURS.PLACEHOLDER }}>Sort by:</span>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="px-4 py-2 rounded-lg focus:outline-none transition-all duration-200 cursor-pointer"
            style={{
              backgroundColor: APP_COLOURS.BG3,
              border: `1px solid ${APP_COLOURS.BG4}`,
              color: APP_COLOURS.TEXTCOLOR,
            }}
          >
            <option value="recent">Most Recent</option>
            <option value="name">Name (A-Z)</option>
            <option value="sales">Sales Value</option>
          </select>
        </div>
      </div>

      {/* Customer List */}
      {sortedCustomers.length === 0 ? (
        <div className="p-12 text-center rounded-lg" style={{ backgroundColor: APP_COLOURS.BG3, border: `1px solid ${APP_COLOURS.BG4}` }}>
          <i className="fas fa-users text-6xl mb-4" style={{ color: APP_COLOURS.PLACEHOLDER }}></i>
          <h3 className="text-xl font-semibold mb-2" style={{ color: APP_COLOURS.TEXTCOLOR }}>
            {searchQuery ? 'No matching customers found' : 'No customers yet'}
          </h3>
          <p className="text-base" style={{ color: APP_COLOURS.PLACEHOLDER }}>
            {searchQuery ? 'Try adjusting your search terms' : 'This staff member hasn\'t captured any customers yet'}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {sortedCustomers.map((customer, index) => (
            <div
              key={customer.id || index}
              className="rounded-lg overflow-hidden transition-all duration-200 cursor-pointer"
              style={{
                backgroundColor: APP_COLOURS.BG3,
                border: `1px solid ${expandedCustomer === customer.id ? APP_COLOURS.BLUE : APP_COLOURS.BG4}`,
              }}
              onClick={() => setExpandedCustomer(expandedCustomer === customer.id ? null : customer.id)}
              onMouseEnter={(e) => {
                if (expandedCustomer !== customer.id) {
                  e.currentTarget.style.borderColor = APP_COLOURS.BG5;
                }
              }}
              onMouseLeave={(e) => {
                if (expandedCustomer !== customer.id) {
                  e.currentTarget.style.borderColor = APP_COLOURS.BG4;
                }
              }}
            >
              {/* Customer Header */}
              <div className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center flex-1">
                    <div className="flex items-center justify-center w-12 h-12 rounded-full mr-4" 
                         style={{ backgroundColor: APP_COLOURS.BLUE, color: 'white' }}>
                      <span className="text-lg font-bold">
                        {customer.name ? customer.name[0].toUpperCase() : 'C'}
                      </span>
                    </div>
                    <div className="flex-1">
                      <h4 className="text-base font-semibold" style={{ color: APP_COLOURS.TEXTCOLOR }}>
                        {customer.name || 'Unknown Customer'}
                      </h4>
                      <p className="text-sm" style={{ color: APP_COLOURS.PLACEHOLDER }}>
                        {customer.email || customer.phone || 'No contact info'}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-4">
                    {customer.totalSales && (
                      <div className="text-right">
                        <p className="text-sm" style={{ color: APP_COLOURS.PLACEHOLDER }}>Total Sales</p>
                        <p className="text-lg font-semibold" style={{ color: APP_COLOURS.BLUE }}>
                          R{customer.totalSales.toFixed(2)}
                        </p>
                      </div>
                    )}
                    <i 
                      className={`fas fa-chevron-${expandedCustomer === customer.id ? 'up' : 'down'}`}
                      style={{ color: APP_COLOURS.PLACEHOLDER }}
                    ></i>
                  </div>
                </div>
              </div>

              {/* Expanded Customer Details */}
              {expandedCustomer === customer.id && (
                <div className="px-4 pb-4 pt-2" style={{ borderTop: `1px solid ${APP_COLOURS.BG4}` }}>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Contact Information */}
                    <div>
                      <h5 className="text-sm font-semibold mb-3" style={{ color: APP_COLOURS.TEXTCOLOR }}>
                        Contact Information
                      </h5>
                      <div className="space-y-2">
                        {customer.email && (
                          <div className="flex items-start">
                            <i className="fas fa-envelope text-sm mt-1 mr-3" style={{ color: APP_COLOURS.BLUE, width: '16px' }}></i>
                            <div>
                              <p className="text-xs" style={{ color: APP_COLOURS.PLACEHOLDER }}>Email</p>
                              <p className="text-sm" style={{ color: APP_COLOURS.TEXTCOLOR }}>{customer.email}</p>
                            </div>
                          </div>
                        )}
                        {customer.phone && (
                          <div className="flex items-start">
                            <i className="fas fa-phone text-sm mt-1 mr-3" style={{ color: APP_COLOURS.BLUE, width: '16px' }}></i>
                            <div>
                              <p className="text-xs" style={{ color: APP_COLOURS.PLACEHOLDER }}>Phone</p>
                              <p className="text-sm" style={{ color: APP_COLOURS.TEXTCOLOR }}>{customer.phone}</p>
                            </div>
                          </div>
                        )}
                        {customer.id_number && (
                          <div className="flex items-start">
                            <i className="fas fa-id-card text-sm mt-1 mr-3" style={{ color: APP_COLOURS.BLUE, width: '16px' }}></i>
                            <div>
                              <p className="text-xs" style={{ color: APP_COLOURS.PLACEHOLDER }}>ID Number</p>
                              <p className="text-sm" style={{ color: APP_COLOURS.TEXTCOLOR }}>{customer.id_number}</p>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Additional Details */}
                    <div>
                      <h5 className="text-sm font-semibold mb-3" style={{ color: APP_COLOURS.TEXTCOLOR }}>
                        Additional Details
                      </h5>
                      <div className="space-y-2">
                        {customer.dateCreated && (
                          <div className="flex items-start">
                            <i className="fas fa-calendar text-sm mt-1 mr-3" style={{ color: APP_COLOURS.BLUE, width: '16px' }}></i>
                            <div>
                              <p className="text-xs" style={{ color: APP_COLOURS.PLACEHOLDER }}>Captured Date</p>
                              <p className="text-sm" style={{ color: APP_COLOURS.TEXTCOLOR }}>
                                {moment(customer.dateCreated.toDate ? customer.dateCreated.toDate() : customer.dateCreated).format('MMMM D, YYYY h:mm A')}
                              </p>
                            </div>
                          </div>
                        )}
                        {customer.transactionCount && (
                          <div className="flex items-start">
                            <i className="fas fa-shopping-cart text-sm mt-1 mr-3" style={{ color: APP_COLOURS.BLUE, width: '16px' }}></i>
                            <div>
                              <p className="text-xs" style={{ color: APP_COLOURS.PLACEHOLDER }}>Transactions</p>
                              <p className="text-sm" style={{ color: APP_COLOURS.TEXTCOLOR }}>{customer.transactionCount}</p>
                            </div>
                          </div>
                        )}
                        {customer.address && (
                          <div className="flex items-start">
                            <i className="fas fa-map-marker-alt text-sm mt-1 mr-3" style={{ color: APP_COLOURS.BLUE, width: '16px' }}></i>
                            <div>
                              <p className="text-xs" style={{ color: APP_COLOURS.PLACEHOLDER }}>Address</p>
                              <p className="text-sm" style={{ color: APP_COLOURS.TEXTCOLOR }}>{customer.address}</p>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Results Count */}
      {sortedCustomers.length > 0 && (
        <div className="mt-6 text-center">
          <p className="text-sm" style={{ color: APP_COLOURS.PLACEHOLDER }}>
            Showing {sortedCustomers.length} of {totalCustomers} customer{totalCustomers !== 1 ? 's' : ''}
          </p>
        </div>
      )}
    </div>
  );
};

export default StaffCustomers;

